import { useMemo } from 'react';
import { Badge, Button, Card } from '@/components/UI';
import DataTable from '@/components/UI/DataTable';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import dayjs from 'dayjs';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import type { OptimizerJobRow } from './types';
import {
    badgeVariantForJobStatus,
    badgeVariantForOutcomeMetric,
    badgeVariantForStrategyExitOutcomeSetStatus,
    badgeVariantForWorkerLiveness,
    formatPct01Number,
    helpTextForOptionObjective,
    labelForOptionObjective,
    labelForOutcomeMetric,
    labelForWorkerLiveness,
    optimizerMaterializationProgress,
    walkForwardBadgeState
} from './helpers';

interface JobsTableCardProps {
    jobs: OptimizerJobRow[];
    isFetching: boolean;
    isError: boolean;
    errorMessage: string | undefined;
    selectedJobId: number | null;
    onSelectJob: (jobId: number) => void;
    onRefresh: () => void;
}

const JobsTableCard = ({ jobs, isFetching, isError, errorMessage, selectedJobId, onSelectJob, onRefresh }: JobsTableCardProps) => {
    const jobsColumns = useMemo((): ColumnDef<OptimizerJobRow>[] => {
        return [
            {
                header: 'ID',
                accessorKey: 'id',
                size: 70,
                maxSize: 80
            },
            {
                header: 'Status',
                accessorKey: 'status',
                size: 140,
                cell: ({ row }) => {
                    const workerLabel = labelForWorkerLiveness(row.original);

                    return (
                        <div className="flex flex-col items-start gap-1">
                            <Badge variant={badgeVariantForJobStatus(row.original.status)}>{row.original.status}</Badge>
                            {workerLabel && (
                                <Badge variant={badgeVariantForWorkerLiveness(row.original.workerLivenessStatus)}>{workerLabel}</Badge>
                            )}
                        </div>
                    );
                }
            },
            {
                header: 'Entity',
                accessorKey: 'entityType',
                size: 120
            },
            {
                header: 'Duration',
                accessorKey: 'duration',
                size: 100
            },
            {
                header: 'Horizon',
                accessorKey: 'horizonSeconds',
                size: 100,
                cell: ({ row }) => (
                    <span className="text-white">
                        {row.original.horizonSeconds != null ? formatTimeHorizon(row.original.horizonSeconds) : '-'}
                    </span>
                )
            },
            {
                header: 'Optimize',
                accessorKey: 'outcomeMetric',
                size: 150,
                cell: ({ row }) => (
                    <div className="flex flex-col gap-1">
                        <Badge variant={badgeVariantForOutcomeMetric(row.original.outcomeMetric)}>
                            {labelForOutcomeMetric(row.original.outcomeMetric)}
                        </Badge>
                        {row.original.outcomeMetric === 'option_profit_percent' && (
                            <span className="text-zinc-500 text-[10px]" title={helpTextForOptionObjective(row.original.optionObjective)}>
                                {labelForOptionObjective(row.original.optionObjective)}
                            </span>
                        )}
                    </div>
                )
            },
            {
                header: 'Objective',
                id: 'objective',
                size: 150,
                enableSorting: false,
                cell: ({ row }) => {
                    if (row.original.outcomeMode !== 'strategy_exit') {
                        return <Badge variant="secondary">Fixed</Badge>;
                    }

                    const outcomeSetId = row.original.strategyExitOutcomeSetId;
                    const status = row.original.strategyExitOutcomeSetStatus;
                    const totalWorkUnits = row.original.strategyExitOutcomeSetMaterializerWorkUnitCount;
                    const completedWorkUnits = row.original.strategyExitOutcomeSetCompletedWorkUnitCount;
                    const failedWorkUnits = row.original.strategyExitOutcomeSetFailedWorkUnitCount;
                    const hasWorkUnits = totalWorkUnits != null && totalWorkUnits > 0;

                    return (
                        <div className="flex flex-col gap-1">
                            <Badge variant={badgeVariantForStrategyExitOutcomeSetStatus(status)}>
                                {outcomeSetId == null ? 'Strategy Exit' : `Exit Set #${outcomeSetId}`}
                            </Badge>
                            <span className="text-zinc-500 text-[10px]">
                                {status ?? 'pending'}
                                {hasWorkUnits
                                    ? ` ${completedWorkUnits ?? 0}/${totalWorkUnits}${failedWorkUnits ? `, ${failedWorkUnits} failed` : ''}`
                                    : ''}
                            </span>
                        </div>
                    );
                }
            },
            {
                header: 'Depth',
                accessorKey: 'maxDepth',
                size: 70
            },
            {
                header: 'Entries',
                accessorKey: 'totalEntries',
                size: 80,
                cell: ({ row }) => (
                    <span className="text-white">
                        {row.original.totalEntries != null ? row.original.totalEntries.toLocaleString() : '-'}
                    </span>
                )
            },
            {
                header: 'Combos',
                accessorKey: 'combinationsEvaluated',
                size: 100,
                cell: ({ row }) => (
                    <span className="text-white">
                        {row.original.combinationsEvaluated != null ? row.original.combinationsEvaluated.toLocaleString() : '-'}
                    </span>
                )
            },
            {
                header: 'Materialization',
                id: 'materialization',
                size: 170,
                enableSorting: false,
                cell: ({ row }) => {
                    const progress = optimizerMaterializationProgress(row.original);

                    return (
                        <div className="min-w-0">
                            <div className="text-white text-xs truncate">
                                {progress.phaseLabel}
                                {progress.percent !== null ? ` ${formatPct01Number(progress.percent, 0)}` : ''}
                            </div>
                            <div className="text-zinc-500 text-[10px] truncate">{progress.detail}</div>
                        </div>
                    );
                }
            },
            {
                header: 'Walk-forward',
                id: 'walkForward',
                size: 170,
                enableSorting: false,
                cell: ({ row }) => {
                    const state = walkForwardBadgeState(row.original);

                    return (
                        <div className="flex min-w-0 flex-col items-start gap-1" title={state.detail}>
                            <Badge variant={state.variant}>{state.label}</Badge>
                            <span className="max-w-40 truncate text-[10px] text-zinc-500">{state.detail}</span>
                        </div>
                    );
                }
            },
            {
                header: 'Runtime',
                accessorKey: 'runtimeSeconds',
                size: 90,
                cell: ({ row }) => (
                    <span className="text-zinc-300">{row.original.runtimeSeconds != null ? `${row.original.runtimeSeconds}s` : '-'}</span>
                )
            },
            {
                header: 'Created',
                accessorKey: 'createdAt',
                size: 170,
                cell: ({ row }) => <span className="text-zinc-300">{dayjs(row.original.createdAt).format('MMM D, YYYY h:mm A')}</span>
            },
            {
                header: 'Actions',
                id: 'actions',
                size: 120,
                cell: ({ row }) => {
                    const jobId = row.original.id;
                    if (jobId == null) return null;

                    return (
                        <Button size="sm" variant={selectedJobId === jobId ? 'primary' : 'secondary'} onClick={() => onSelectJob(jobId)}>
                            {selectedJobId === jobId ? 'Selected' : 'Select'}
                        </Button>
                    );
                }
            }
        ];
    }, [onSelectJob, selectedJobId]);

    return (
        <Card className="bg-zinc-900 mb-6">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-semibold">Optimizer Jobs</h4>
                <Button disabled={isFetching} size="sm" variant="secondary" onClick={onRefresh}>
                    {isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
            </div>
            {isError && <div className="text-red-300 text-sm mb-2">Failed to load jobs: {errorMessage ?? 'Unknown error'}</div>}
            {jobs.length === 0 && !isError ? (
                <div className="text-zinc-400 text-sm">No optimizer jobs yet.</div>
            ) : jobs.length > 0 ? (
                <DataTable allowOverflow columns={jobsColumns} data={jobs} pagination={{ enabled: true, pageSize: 8 }} search={false} />
            ) : null}
        </Card>
    );
};

export default JobsTableCard;
